import React from "react";
import { useQuery } from "@tanstack/react-query";
import Background from "../Background";
import ContainerEffectOverlay from "../ContainerEffectOverlay";
import ProgressiveHeroImage from "../ProgressiveHeroImage";
import {
  SlotRenderer,
  EditableSlotMediaOverlay,
  getHeroImageStyle,
  useHeroAnimation,
  useHeroAmbient,
} from "./heroModeUtils";
import { apiFetch } from "../../../core/utils/api";
import "./heroModeAnimations.css";

/**
 * HeroProfile — renderer for ``profile`` (static) and
 * ``profile_animated`` (gentle pulse) modes.
 *
 * A portrait sits at the top of a centered content column, followed
 * by name (heading), tagline (body), and a primary CTA. The default
 * mode masks it to a circle; the ``profile_rounded`` sibling uses a
 * rounded square for a more editorial headshot frame. Both shapes
 * crop with ``object-fit: cover`` so users don't have to prepare a
 * perfectly matched source image.
 *
 * The portrait slot accepts a single ``image`` item. When the slot is
 * empty in edit mode the standard ``EmptySlotPlaceholder`` is shown so
 * editors can drop a portrait in with one click; in render mode an
 * empty portrait simply collapses out of the layout (heading / body /
 * cta still center).
 */

// ---------------------------------------------------------------------------
// Breakpoint config
// ---------------------------------------------------------------------------
//
// Portrait sizes are deliberately conservative — too large and the
// composition reads as "photo with caption" rather than a balanced
// profile hero. Vertical padding scales with viewport so the column
// breathes on tall screens without going edge-to-edge on mobile.
const BP_CONFIG = {
  extra_large: { portraitPx: 200, gap: 28, vertPadding: 80, bodyMaxWidth: 560 },
  large:       { portraitPx: 180, gap: 24, vertPadding: 64, bodyMaxWidth: 520 },
  medium:      { portraitPx: 160, gap: 20, vertPadding: 48, bodyMaxWidth: 480 },
  small:       { portraitPx: 140, gap: 18, vertPadding: 40, bodyMaxWidth: "calc(100% - 48px)" },
  extra_small: { portraitPx: 120, gap: 16, vertPadding: 32, bodyMaxWidth: "calc(100% - 32px)" },
};

// ---------------------------------------------------------------------------
const PORTRAIT_SHAPES = {
  circle: {
    radius: "9999px",
    className: "rounded-full",
  },
  rounded_square: {
    // Percentage radius scales with the frame size at every breakpoint.
    radius: "22%",
    className: "rounded-[22%]",
  },
};

function resolvePortraitShape(heroMode) {
  return heroMode === "profile_rounded" ? "rounded_square" : "circle";
}

// Portrait renderer (internal)
// ---------------------------------------------------------------------------
//
// The Hero serializer embeds image_url / cropped_image_url on its
// minimal child refs (see ``HeroSerializer._build_minimal_child_ref``)
// so the common case is render-from-ref with zero network traffic.
// We still defensively fetch the full payload if those fields are
// missing — this happens for legacy refs and during certain editor
// flows where the slot was just created.
function ProfilePortrait({ slotData, sizePx, ambientClass, shape, heroItem }) {
  const shapeConfig = PORTRAIT_SHAPES[shape] || PORTRAIT_SHAPES.circle;
  const hasFullData =
    slotData &&
    Object.keys(slotData).some((k) => !["id", "type", "order"].includes(k));

  const { data: fetchedItem } = useQuery({
    queryKey: ["item", slotData?.type, slotData?.id],
    queryFn: async () => {
      const res = await apiFetch(`/api/items/${slotData.type}/${slotData.id}/`);
      if (!res.ok)
        throw new Error(`Failed to fetch ${slotData.type} ${slotData.id}`);
      return res.json();
    },
    enabled: !!slotData?.id && !!slotData?.type && !hasFullData,
    staleTime: 30_000,
  });

  const resolved = !hasFullData && fetchedItem ? fetchedItem : slotData;
  const src = resolved?.cropped_image_url || resolved?.image_url || null;
  const alt = resolved?.alt_text || "";
  const { className: filterClass, style: filterStyle } =
    getHeroImageStyle(resolved);

  if (!src) {
    return (
      <div
        className={`${shapeConfig.className} bg-white/15 dark:bg-white/10 ${ambientClass}`}
        style={{
          width: `${sizePx}px`,
          height: `${sizePx}px`,
          borderRadius: shapeConfig.radius,
          flexShrink: 0,
        }}
      />
    );
  }

  return (
    <div
      className={`${shapeConfig.className} overflow-hidden relative ${ambientClass}`}
      style={{
        width: `${sizePx}px`,
        height: `${sizePx}px`,
        borderRadius: shapeConfig.radius,
        flexShrink: 0,
        boxShadow: "0 8px 32px rgba(0,0,0,0.18), 0 2px 6px rgba(0,0,0,0.12)",
      }}
    >
      <ProgressiveHeroImage
        src={src}
        alt={alt}
        loading="lazy"
        draggable={false}
        className={filterClass}
        style={{
          width: "100%",
          height: "100%",
          objectFit: "cover",
          display: "block",
          ...filterStyle,
        }}
      />
      <EditableSlotMediaOverlay slotData={resolved} heroItem={heroItem} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// HeroProfile main renderer
// ---------------------------------------------------------------------------

const HeroProfile = ({ item, heroBase, heroSlots }) => {
  const {
    heroRootRef, containerClasses, containerStyle, fullBleedStyle,
    contentInsetStyle, responsiveHeight, baseHeightPx, bp,
  } = heroBase;

  const isAnimated = heroSlots?.isAnimated || false;
  const portraitSlotData = heroSlots?.getSlot("portrait");
  const portraitShape = resolvePortraitShape(item?.hero_mode);

  const config = BP_CONFIG[bp] || BP_CONFIG.medium;

  const entrance = heroSlots?.animatedEntrance || "scale-in";
  // Profile's ambient (pulse, shipped with ``profile_animated``)
  // attaches to the portrait only — keeps the photo "alive" without
  // making the body text bob with it.
  const portraitAmbientClass = useHeroAmbient(heroSlots?.ambientAnimation);

  const portraitAnim = useHeroAnimation(isAnimated, entrance, { delay: 0 });
  const headingAnim = useHeroAnimation(isAnimated, entrance, { delay: 150 });
  const bodyAnim = useHeroAnimation(isAnimated, entrance, { delay: 300 });
  const ctaAnim = useHeroAnimation(isAnimated, entrance, { delay: 450 });

  const bodyMaxWidth =
    typeof config.bodyMaxWidth === "number"
      ? `${config.bodyMaxWidth}px`
      : config.bodyMaxWidth;

  return (
    <div
      ref={heroRootRef}
      data-hero-root="true"
      className={containerClasses}
      style={{ ...containerStyle, ...fullBleedStyle }}
    >
      <Background
        bgImage={heroBase.bgImage}
        imageStyleClasses={heroBase.imageStyleClasses}
        imageInlineStyles={heroBase.imageInlineStyles}
        colorClass={heroBase.colorClass}
        colorStyle={heroBase.colorStyle}
        jsPatternLayer={heroBase.jsPatternLayer}
      />
      <ContainerEffectOverlay data={heroBase.data} />

      <div
        className={
          responsiveHeight
            ? "relative z-10 flex items-center justify-center w-full"
            : "absolute inset-0 z-10 flex items-center justify-center"
        }
        style={responsiveHeight ? { minHeight: `${baseHeightPx}px` } : undefined}
      >
        <div
          className="flex flex-col items-center text-center w-full mx-auto"
          style={{
            paddingTop: config.vertPadding,
            paddingBottom: config.vertPadding,
            gap: config.gap,
            ...contentInsetStyle,
          }}
        >
          {/* Portrait. Always rendered (even when empty in edit mode)
              so the EmptySlotPlaceholder can suggest adding a photo. */}
          <div ref={portraitAnim.ref} className={portraitAnim.className} style={portraitAnim.style}>
            {portraitSlotData ? (
              <ProfilePortrait
                slotData={portraitSlotData}
                sizePx={config.portraitPx}
                ambientClass={portraitAmbientClass}
                shape={portraitShape}
                heroItem={item}
              />
            ) : (
              <SlotRenderer
                slotName="portrait"
                heroSlots={heroSlots}
                heroBase={heroBase}
                heroId={item?.id}
                slotIndex={0}
              />
            )}
          </div>

          <div ref={headingAnim.ref} className={`w-full ${headingAnim.className}`} style={headingAnim.style}>
            <SlotRenderer
              slotName="heading"
              heroSlots={heroSlots}
              heroBase={heroBase}
              heroId={item?.id}
              slotIndex={1}
            />
          </div>

          <div
            ref={bodyAnim.ref}
            className={`mx-auto ${bodyAnim.className}`}
            style={{ maxWidth: bodyMaxWidth, ...bodyAnim.style }}
          >
            <SlotRenderer
              slotName="body"
              heroSlots={heroSlots}
              heroBase={heroBase}
              heroId={item?.id}
              slotIndex={2}
            />
          </div>

          <div ref={ctaAnim.ref} className={ctaAnim.className} style={ctaAnim.style}>
            <SlotRenderer
              slotName="cta"
              heroSlots={heroSlots}
              heroBase={heroBase}
              heroId={item?.id}
              slotIndex={3}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default React.memo(HeroProfile);
